import * as React from 'react';
import { useHistory } from 'react-router-dom';

import { DateRangeOptions, IDateRangeSettings } from '@frontend/app/components';
import { useDateFilterSettings } from '@frontend/app/hooks';

// Minimal data from offer needed
interface IContextOffer {
  id: number;
  name: string;
  source: string;
}

type TRefreshFunction = () => void;

interface IPaymentsDueLoading {
  state: 'loading';
}
interface IPaymentsDueReady {
  paymentsDue: number;
  state: 'ready';
}
interface IPaymentsDueFailed {
  errorMessage: string;
  state: 'failed';
}

export type TPaymentsDue =
  | IPaymentsDueLoading
  | IPaymentsDueReady
  | IPaymentsDueFailed;

interface IUnsavedChangesState {
  hasUnsavedChanges: boolean;
  showUnsavedChangesDialog: boolean;
  onConfirm?: () => void;
}

interface ISTAPaymentAppContext {
  dateFilter: IDateRangeSettings;
  offers: IContextOffer[];
  setOffers: (offers: IContextOffer[]) => void;
  paymentsDue: TPaymentsDue;
  setPaymentsDue: (paymentsDue: TPaymentsDue) => void;
  refreshCurrentDashboard: TRefreshFunction;
  setRefreshCurrentDashboard: (refresh: TRefreshFunction) => void;
  refreshCurrentTable: TRefreshFunction;
  setRefreshCurrentTable: (refresh: TRefreshFunction) => void;
  unsavedChanges: IUnsavedChangesState;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  handleBackWithConfirmation: (onConfirm: () => void) => void;
  handleDiscardChanges: () => void;
  handleCancelDiscard: () => void;
}

const { useContext, useState, useCallback } = React;

const STAPaymentAppContext = React.createContext<ISTAPaymentAppContext>(null);

export const useSTAPaymentAppContext = () => useContext(STAPaymentAppContext);

export const STAPaymentAppContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dateFilter = useDateFilterSettings(DateRangeOptions.ALL_TIME);
  const [offers, setOffers] = useState<IContextOffer[]>([]);
  const [paymentsDue, setPaymentsDue] = useState<TPaymentsDue>({ state: 'loading' });
  const [refreshCurrentDashboard, setRefreshCurrentDashboard] = useState<TRefreshFunction>();
  const [refreshCurrentTable, setRefreshCurrentTable] = useState<TRefreshFunction>();

  const [unsavedChanges, setUnsavedChanges] = useState<IUnsavedChangesState>({
    hasUnsavedChanges: false,
    showUnsavedChangesDialog: false,
    onConfirm: undefined
  });

  const history = useHistory();

  const setHasUnsavedChanges = useCallback((hasChanges: boolean) => {
    setUnsavedChanges((prev) => ({
      ...prev,
      hasUnsavedChanges: hasChanges
    }));
  }, []);

  const handleBackWithConfirmation = useCallback((onConfirm: () => void) => {
    if (unsavedChanges.hasUnsavedChanges) {
      setUnsavedChanges((prev) => ({
        ...prev,
        showUnsavedChangesDialog: true,
        onConfirm
      }));
      return;
    }
    onConfirm();
  }, [unsavedChanges.hasUnsavedChanges]);

  const handleDiscardChanges = useCallback(() => {
    const { onConfirm } = unsavedChanges;
    setUnsavedChanges((prev) => ({
      ...prev,
      hasUnsavedChanges: false,
      showUnsavedChangesDialog: false,
      onConfirm: undefined
    }));

    if (onConfirm) {
      onConfirm();
    } else {
      history.goBack();
    }
  }, [unsavedChanges, history]);

  const handleCancelDiscard = useCallback(() => {
    setUnsavedChanges((prev) => ({
      ...prev,
      showUnsavedChangesDialog: false,
      onConfirm: undefined
    }));
  }, []);

  return (
    <STAPaymentAppContext.Provider
      value={{
        dateFilter,
        offers,
        setOffers,
        paymentsDue,
        setPaymentsDue,
        refreshCurrentDashboard,
        setRefreshCurrentDashboard,
        refreshCurrentTable,
        setRefreshCurrentTable,
        unsavedChanges,
        setHasUnsavedChanges,
        handleBackWithConfirmation,
        handleDiscardChanges,
        handleCancelDiscard,
      }}
    >
      {children}
    </STAPaymentAppContext.Provider>
  );
};
